import React, { useState } from "react";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Tab, Paper, Typography, Chip, Divider, Avatar, TextField, Button } from "@mui/material";
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/auth/auth-provider";
import { useToast } from "../../context/toast/toast-provider";
import ViewLoader from "../../component/misc/view-loader";
import { Role } from "../enum/role";
import { updateProfile } from "../-account/service/user-service";
import commonStyles from "../style/common.module.css";

const TAB_BASIC_INFO = "info";
const TAB_EARNINGS = "earnings";
const QUERY_PARAM_TAB = "tab";

export default function ProfileView() {
    const auth = useAuth();
    const toast = useToast();
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState<string>(searchParams.get(QUERY_PARAM_TAB) ?? TAB_BASIC_INFO);
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(auth.user?.name ?? "");
    const [phone, setPhone] = useState(auth.user?.phone ?? "");
    const [saving, setSaving] = useState(false);

    const user = auth.user;
    const isLoropioGirl = user?.roles.includes(Role.LoropioGirlsExplorer) ?? false;
    const isExplorer = user?.roles.includes(Role.Explorer) ?? false;
    const showEarnings = !isLoropioGirl && !isExplorer;

    const getRoleNames = () => {
        return user?.roles.map(r => Role[r]) ?? [];
    };

    const handleSave = () => {
        if (!name.trim()) return;
        setSaving(true);
        updateProfile({ name: name.trim(), phone: phone.trim() || undefined })
            .then(() => {
                setEditing(false);
                setSaving(false);
                toast.addToast(`Profile updated`, "success");
                auth.updateUser({ name: name.trim(), phone: phone.trim() || undefined });
            })
            .catch(() => {
                setSaving(false);
                toast.addToast(`Unable to update profile`, "error");
            });
    };

    const handleCancel = () => {
        setName(user?.name ?? "");
        setPhone(user?.phone ?? "");
        setEditing(false);
    };

    return (
        <React.Fragment>
            <ViewLoader loading={false} />
            <div className={commonStyles.content}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar sx={{ width: 64, height: 64, bgcolor: 'var(--color-primary)' }}>
                        <PersonIcon sx={{ fontSize: 36 }} />
                    </Avatar>
                    <div>
                        <Typography variant="h4">{user?.name}</Typography>
                        <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
                    </div>
                </Box>

                <TabContext value={tab}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <TabList onChange={(_event: React.SyntheticEvent, newTab: string) => {
                            setTab(newTab);
                            setSearchParams({ [QUERY_PARAM_TAB]: newTab });
                        }}>
                            <Tab label="Basic Information" value={TAB_BASIC_INFO} />
                            {showEarnings && <Tab label="Earnings" value={TAB_EARNINGS} />}
                        </TabList>
                    </Box>

                    <TabPanel value={TAB_BASIC_INFO}>
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6" gutterBottom>Personal Details</Typography>
                                {!editing && (
                                    <Button size="small" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
                                        Edit
                                    </Button>
                                )}
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            {editing ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
                                    <TextField
                                        label="Name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        size="small"
                                        error={!name.trim()}
                                        helperText={!name.trim() ? "Name is required" : ""}
                                    />
                                    <TextField
                                        label="Phone"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        size="small"
                                    />
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button variant="contained" onClick={handleSave} disabled={saving || !name.trim()}>
                                            {saving ? "Saving..." : "Save"}
                                        </Button>
                                        <Button variant="outlined" onClick={handleCancel} disabled={saving}>
                                            Cancel
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Lion ID</Typography>
                                        <Typography variant="body1">{user?.id}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Name</Typography>
                                        <Typography variant="body1">{user?.name}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Email</Typography>
                                        <Typography variant="body1">{user?.email ?? "Not set"}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Phone</Typography>
                                        <Typography variant="body1">{user?.phone ?? "Not set"}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Cohort</Typography>
                                        <Typography variant="body1">{user?.cohortName ?? "Not assigned"}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Status</Typography>
                                        <Chip
                                            label={user?.active ? "Active" : "Inactive"}
                                            color={user?.active ? "success" : "default"}
                                            size="small"
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Paper>

                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" gutterBottom>Roles</Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {getRoleNames().map(role => (
                                    <Chip key={role} label={role} color="primary" variant="outlined" />
                                ))}
                            </Box>
                        </Paper>
                    </TabPanel>

                    {showEarnings && <TabPanel value={TAB_EARNINGS}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>Earnings</Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="body2" color="text.secondary">
                                Earnings information will be available here soon.
                            </Typography>
                        </Paper>
                    </TabPanel>}
                </TabContext>
            </div>
        </React.Fragment>
    );
}
